import dayjs from "dayjs";

import type * as T from "@src/types";
import { isPositionTrackingEnded } from "@src/utils/positionStatus";

export * from "./cacheData";
export * from "./crosshairTracking";
export * from "./tvAdvanced";
export * from "./tvLight";

const getDefaultRangeFromNow = (nDays = 7) => {
  console.warn("No candle/position data available to set initial range, defaulting", nDays, "days");
  const nowTimestamp = dayjs().unix();

  return {
    from: nowTimestamp - 86400 * nDays,
    to: nowTimestamp,
  };
};

/**
 * The range to reopen a rebuilt chart on. A viewport that showed the newest bar of the old data follows
 * the newest bar of the new data, so a refreshed live chart keeps showing the latest candles.
 */
export const restoreVisibleRange = (
  saved: T.SavedViewport,
  latestTime: number | undefined,
): T.TV.VisibleTimeRange => {
  const { range, latestTime: savedLatestTime } = saved;
  if (
    latestTime &&
    savedLatestTime &&
    range.to >= savedLatestTime &&
    latestTime > savedLatestTime
  ) {
    const shift = latestTime - savedLatestTime;
    return { from: range.from + shift, to: range.to + shift };
  }
  return range;
};

export const getInitialRangeFromCandles = (candles: T.TypedArrayCandles, nbDays = 1) => {
  const { times } = candles;
  const range = getDefaultRangeFromNow();

  if (times && times.length > 0) {
    // Start at the LAST/most recent position (first element due to TV's reverse ordering)
    const latestDataTime = times.at(0)!;

    range.from = latestDataTime - 86400 * nbDays;
    range.to = latestDataTime;
  }
  return range;
};

export const getInitialRangeFromPositions = (
  positions: T.AppPosition[],
  tvResolution: T.TV.ResolutionString,
  // Only for a page showing one position on its own, where the entry is the subject. A run is read
  // the other way round: you want its latest action first and scroll back for history.
  // Tracking-ended positions are dropped before framing.
  openOnEntry = false,
) => {
  positions = positions.filter((position) => !isPositionTrackingEnded(position));
  if (!positions.length) {
    return getDefaultRangeFromNow();
  }

  const now = dayjs().unix();
  const hasOpenPosition = positions.some((p) => !p.end_ts);

  // Calculate actual position span (earliest start to latest end)
  const timestamps = positions.flatMap((p) => {
    const start = dayjs(p.start_ts).unix();
    // An open position runs to now, so that is its end for framing purposes. Treating it as ending
    // at its own entry gives a span of zero, and the padding floor below then frames a five-minute
    // window either side of one candle.
    const end = p.end_ts ? dayjs(p.end_ts).unix() : now;
    return [start, end];
  });

  const earliestTime = Math.min(...timestamps);
  const latestTime = Math.max(...timestamps);
  const span = latestTime - earliestTime;

  const daysSincePosition = dayjs().diff(dayjs.unix(latestTime), "days");

  // Skip range adjustment if positions are recent (within 5 days) - let TradingView use current data.
  // An open position always looks recent by that measure, since it runs to now, but its entry can
  // still be months back and has to stay on screen, so it is framed rather than left to TradingView.
  // A page showing one position never skips: TradingView's default is the newest candles, which for
  // a trade that closed a few hours ago sits past the exit and leaves the position off screen.
  if (!openOnEntry && !hasOpenPosition && daysSincePosition < 5) {
    return;
  }

  // Define max comfortable view based on resolution
  let maxViewSeconds: number;
  if (tvResolution.endsWith("D") || tvResolution.endsWith("W") || tvResolution.endsWith("M")) {
    maxViewSeconds = 86400 * 90; // 90 days for daily/weekly/monthly
  } else {
    const minutes = parseInt(tvResolution, 10) || 1;
    if (minutes <= 5) {
      maxViewSeconds = 86400 * 1; // 1 day for 1m-5m
    } else if (minutes <= 60) {
      maxViewSeconds = 86400 * 7; // 1 week for 15m-1h
    } else {
      maxViewSeconds = 86400 * 30; // 1 month for 2h-12h
    }
  }

  let range: { from: number; to: number };

  if (span <= maxViewSeconds) {
    // Short span: show full range with 15% padding (min 5 minutes)
    const padding = Math.max(span * 0.15, 300);
    range = {
      from: earliestTime - padding,
      to: latestTime + padding,
    };
  } else if (openOnEntry) {
    // One position, too long to show at once. Open on the entry, because that is where the decision
    // was made and the trade runs forward from it. A short lead-in gives the entry some context.
    const leadIn = maxViewSeconds * 0.15;
    range = {
      from: earliestTime - leadIn,
      to: earliestTime + (maxViewSeconds - leadIn),
    };
  } else {
    // Long span: show most recent portion, weighted toward history before latest position
    // Shows 50% of maxView before latestTime, 10% after (asymmetric to emphasize lead-up)
    const windowBeforeLatest = maxViewSeconds / 2;
    range = {
      from: latestTime - windowBeforeLatest,
      to: latestTime + windowBeforeLatest * 0.2,
    };
  }

  return range;
};
